import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthSelectors } from 'src/app/store/auth/auth.selectors';
import { environment } from 'src/environments/environment';

interface DiscountEligibilityResponse {
  allowed: boolean;
  companyId: string;
}

/**
 * Handles eligibility checks for enabling coupon functionality.
 */
@Injectable({ providedIn: 'root' })
export class DiscountEligibilityService {
  constructor(
    private readonly http: HttpClient,
    private readonly store: Store,
  ) { }

  /**
   * Determines if the current company is eligible for coupons.
   *
   * @param companyId Optional company ID. If not provided, will be retrieved from the auth store.
   * @returns Observable that emits true if discounts are allowed, false otherwise.
   * @throws Error if the request fails (e.g., CORS, network, or server errors).
   */
  isDiscountAllowed(companyId?: string): Observable<boolean> {
    const resolvedCompanyId = companyId || this.getCompanyIdFromStore();
    if (!resolvedCompanyId) {
      return of(false);
    }

    const url = this.buildRequestUrl(resolvedCompanyId);
    return this.http.get<DiscountEligibilityResponse>(url).pipe(
      map(response => {
        return response?.allowed === true;
      }),
      catchError(error => throwError(() => error)),
    );
  }

  /**
   * Builds the request URL with the company ID parameter.
   * Note: The HTTP interceptor may also add a 'company' parameter automatically.
   *
   * CORS Configuration Required:
   * The backend server must be configured to allow requests from the frontend origin.
   * For local development, the server at the discountEligibilityApiUrl must:
   * - Set Access-Control-Allow-Origin header to the frontend origin (e.g., 'http://localhost:4300')
   * - Handle preflight OPTIONS requests properly
   * - Set other required CORS headers (Access-Control-Allow-Methods, Access-Control-Allow-Headers, etc.)
   */
  private buildRequestUrl(companyId: string): string {
    const baseUrl = environment.discountEligibilityApiUrl;

    if (baseUrl.includes('{companyId}')) {
      return baseUrl.replace('{companyId}', encodeURIComponent(companyId));
    }

    if (baseUrl.includes('companyId=')) {
      return baseUrl.endsWith('=')
        ? `${baseUrl}${encodeURIComponent(companyId)}`
        : `${baseUrl}&companyId=${encodeURIComponent(companyId)}`;
    }

    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}companyId=${encodeURIComponent(companyId)}`;
  }

  private getCompanyIdFromStore(): string | null {
    return this.store.selectSnapshot(AuthSelectors.company)?.id ?? null;
  }
}
